import React, { useState } from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  NumberField,
  BooleanField,
  DateField,
  FunctionField,
  useRecordContext,
  TabbedShowLayout,
  Tab,
  ReferenceField,
  ReferenceManyField,
  Datagrid,
  EditButton,
  DeleteButton,
  Button,
  TopToolbar,
  useRefresh,
  useNotify,
  useUpdate,
  useGetOne,
  ReferenceInput,
  SelectInput,
  useGetList,
} from 'react-admin';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Card,
  CardContent,
  Divider,
  IconButton,
  Button as MuiButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  ImageList,
  ImageListItem,
  Stack,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Build as MaintenanceIcon,
  LocalGasStation as FuelIcon,
  Assignment as InspectionIcon,
  PersonAdd as AssignmentIcon,
  Warning as IncidentIcon,
  Info as InfoIcon,
  Add as CreateIcon,
  Edit as EditIcon,
  LocalShipping as VehicleIcon,
  PhotoCamera as PhotoIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
  NavigateNext as NavigateNextIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  GetApp as DownloadIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Work as WorkIcon,
  Inventory as InventoryIcon,
  Notifications as RemindersIcon,
  Description as FilesIcon,
  Assignment as CompletedFormsIcon,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Person as PersonIcon,
  DirectionsCar as DirectionsCarIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  PhotoCamera as PhotoCameraIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Shield as ShieldIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { MaintenanceDialog, MaintenanceShowDialog, FuelDialog, FuelShowDialog, InspectionDialog, InspectionShowDialog, AssignmentDialog, IncidentDialog, IncidentShowDialog } from '../../components/dialogs';
import { CompletedFormDialog } from '../../components/dialogs/CompletedFormDialog';
import ResourceBreadcrumbs from '../../components/ResourceBreadcrumbs';
import { RemindersTab } from '../../components/RemindersTab';
import { EquipmentDocuments } from '../../components/EquipmentDocuments';
import { EquipmentPrimaryPhoto } from '../../components/EquipmentPrimaryPhoto';
import { EquipmentPrimaryFiles } from '../../components/EquipmentPrimaryFiles';

// Safe wrapper for TextField to ensure string values
const SafeTextField = ({ source, ...props }: any) => {
  const record = useRecordContext();
  const value = record?.[source];
  
  if (!value) return <span></span>;
  
  // Ensure the value is a string
  const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
  
  return <span>{stringValue}</span>;
};

// Safe wrapper for NumberField to handle Decimal.js objects
const SafeNumberField = ({ source, ...props }: any) => {
  const record = useRecordContext();
  const value = record?.[source];
  
  if (!value) return <span></span>;
  
  // Handle Decimal.js objects (which have s, e, d properties)
  if (typeof value === 'object' && value.s !== undefined && value.e !== undefined && value.d !== undefined) {
    // Convert Decimal object to number
    const numberValue = Number(value);
    return <span>{isNaN(numberValue) ? '' : numberValue.toLocaleString()}</span>;
  }

  // Handle regular numbers
  if (typeof value === 'number') {
    return <span>{value.toLocaleString()}</span>;
  }

  // Try to convert string to number
  const numberValue = Number(value);
  return <span>{isNaN(numberValue) ? '' : numberValue.toLocaleString()}</span>;
};

// File Display Component
interface FileDisplayProps {
  fileId?: string | number;
  label: string;
  onDelete?: () => void;
}

const FileDisplay: React.FC<FileDisplayProps> = ({ fileId, label, onDelete }) => {
  const [fileData, setFileData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const notify = useNotify();

  const isImage = (filename: string) => {
    return /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(filename);
  };

  const fetchFileData = async () => {
    if (!fileId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_SIMPLE_REST_URL}/files/${fileId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setFileData(data);
      }
    } catch (error) {
      console.error('Error fetching file data:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchFileData();
  }, [fileId]);

  const handleView = () => {
    setViewDialogOpen(true);
  };

  const handleDownload = () => {
    if (fileData?.url) {
      window.open(fileData.url, '_blank');
    }
  };

  if (!fileId || !fileData) {
    return (
      <Box sx={{ 
        textAlign: 'center', 
        p: 4, 
        border: '2px dashed #e0e0e0', 
        borderRadius: 2,
        backgroundColor: '#fafafa',
        minHeight: 150,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <InsertDriveFileIcon sx={{ fontSize: 56, color: '#e0e0e0', mb: 2 }} />
        <Typography variant="body1" color="text.secondary" gutterBottom>
          No {label.toLowerCase()} uploaded
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Upload documents in the edit view
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
        {/* File Preview */}
        <Box 
          sx={{ 
            height: 200, 
            backgroundColor: '#f5f5f5', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            cursor: 'pointer',
            '&:hover': { backgroundColor: '#f0f0f0' }
          }}
          onClick={handleView}
        >
          {loading ? (
            <CircularProgress />
          ) : isImage(fileData.name) ? (
            <img 
              src={fileData.url} 
              alt={fileData.name}
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%', 
                objectFit: 'contain' 
              }}
            />
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <InsertDriveFileIcon sx={{ fontSize: 64, color: '#1976d2', mb: 1 }} />
              <Typography variant="body2">{fileData.name}</Typography>
            </Box>
          )}
        </Box>

        {/* File Info & Actions */}
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" noWrap title={fileData.name}>
            {fileData.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fileData.size ? `${(fileData.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button 
              size="small" 
              startIcon={<ViewIcon />} 
              onClick={handleView}
            >
              View
            </Button>
            <Button 
              size="small" 
              startIcon={<DownloadIcon />} 
              onClick={handleDownload}
            >
              Download
            </Button>
            {onDelete && (
              <Button 
                size="small" 
                color="error" 
                startIcon={<DeleteIcon />} 
                onClick={onDelete}
              >
                Delete
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* View Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {fileData?.name}
            <IconButton onClick={() => setViewDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {isImage(fileData?.name || '') ? (
            <img 
              src={fileData?.url} 
              alt={fileData?.name}
              style={{ width: '100%', height: 'auto' }}
            />
          ) : (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <InsertDriveFileIcon sx={{ fontSize: 96, color: '#1976d2', mb: 2 }} />
              <Typography variant="h6">{fileData?.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This file type cannot be previewed. Click download to view the file.
              </Typography>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
                Download File
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// Job Types Section Component (View Only)
const JobTypesSection = () => {
  const record = useRecordContext();

  const utilizations = record?.utilizations || [];

  return (
    <Card sx={{ mb: 3, border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <WorkIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
          <Typography variant="h5" fontWeight="bold" color="primary">
            Job Types
          </Typography>
        </Box>
        
        {utilizations.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {utilizations.map((item: any, index: number) => (
              <Chip
                key={index}
                label={item.utilization?.name || item.name || 'Unknown'}
                color="primary"
                variant="outlined"
                sx={{ mb: 1 }}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ 
            textAlign: 'center', 
            p: 3, 
            backgroundColor: '#fafafa',
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <WorkIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No job types assigned
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Add job types in the edit view
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Commodities Section Component (View Only)
const CommoditiesSection = () => {
  const record = useRecordContext();

  const commodities = record?.commodities || [];

  return (
    <Card sx={{ mb: 3, border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <InventoryIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
          <Typography variant="h5" fontWeight="bold" color="primary">
            Commodities Hauled
          </Typography>
        </Box>

        {commodities.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {commodities.map((item: any, index: number) => (
              <Chip
                key={index}
                label={item.commodity?.name || item.name || 'Unknown'}
                color="secondary"
                variant="outlined"
                sx={{ mb: 1 }}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{
            textAlign: 'center',
            p: 3,
            backgroundColor: '#fafafa',
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <InventoryIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No commodities assigned
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Add commodities in the edit view
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Insurance Details Dialog
const InsuranceDetailsDialog = ({ open, onClose, insurance }: {
  open: boolean;
  onClose: () => void;
  insurance: any;
}) => {
  const formatCurrency = (value: any) => {
    if (!value || isNaN(Number(value))) return '-';
    return `$${Number(value).toLocaleString()}`;
  };

  const getExpiryStatus = (expiryDate: string) => {
    if (!expiryDate) return { color: 'default', label: 'No expiry date' };

    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return { color: 'error', label: 'Expired' };
    } else if (daysUntilExpiry <= 30) {
      return { color: 'warning', label: `Expires in ${daysUntilExpiry} days` };
    } else {
      return { color: 'success', label: 'Active' };
    }
  };

  if (!insurance) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Insurance Policy Details</Typography>
            <Typography variant="body2" color="text.secondary">
              {insurance.policyNumber} - {insurance.insurerName}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {/* Policy Information Section */}
          <Typography variant="subtitle1" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
            Policy Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Policy Number
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {insurance.policyNumber || '-'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={getExpiryStatus(insurance.expiryDate).label}
                    color={getExpiryStatus(insurance.expiryDate).color as any}
                    size="small"
                  />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Insurance Company
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {insurance.insurerName || '-'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Policy Name
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {insurance.policyName || '-'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Coverage Dates Section */}
          <Typography variant="subtitle1" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
            Coverage Dates
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Effective Date
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {insurance.effectiveDate ? new Date(insurance.effectiveDate).toLocaleDateString() : '-'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Expiry Date
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {insurance.expiryDate ? new Date(insurance.expiryDate).toLocaleDateString() : '-'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Coverage Limits & Deductibles Section */}
          <Typography variant="subtitle1" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
            Coverage Limits & Deductibles
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Liability Limit
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {formatCurrency(insurance.liabilityLimit)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Collision Deductible
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {formatCurrency(insurance.collisionDeductible)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Comprehensive Deductible
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {formatCurrency(insurance.comprehensiveDeductible)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  All Perils Deductible
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {formatCurrency(insurance.allPerilsDeductible)}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Notes Section */}
          {insurance.notes && (
            <>
              <Typography variant="subtitle1" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
                Notes
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, mb: 2 }}>
                <Typography variant="body1">
                  {insurance.notes}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>Close</MuiButton>
      </DialogActions>
    </Dialog>
  );
};

// CVOR Section Component (View Only)
const CvorSection = () => {
  const record = useRecordContext();
  const cvor = record?.cvor;

  return (
    <Card sx={{ mb: 3, border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <BadgeIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
          <Typography variant="h5" fontWeight="bold" color="primary">
            CVOR
          </Typography>
        </Box>

        {cvor ? (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  CVOR/NSC Number
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {cvor.cvorNscNumber}
                  {cvor.active === false && (
                    <Chip label="Inactive" size="small" sx={{ ml: 1 }} />
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Legal Operating Name
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {cvor.legalOperatingName}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{
            textAlign: 'center',
            p: 3,
            backgroundColor: '#fafafa',
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <BadgeIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No CVOR assigned
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Assign a CVOR in the edit view
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Insurance Section Component (View Only)
const InsuranceSection = () => {
  const record = useRecordContext();
  const [dialogOpen, setDialogOpen] = useState(false);

  const insurance = record?.insurance;

  const getExpiryStatus = (expiryDate: string) => {
    if (!expiryDate) return { color: 'default', label: 'No expiry date' };

    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return { color: 'error', label: 'Expired' };
    } else if (daysUntilExpiry <= 30) {
      return { color: 'warning', label: `Expires in ${daysUntilExpiry} days` };
    } else {
      return { color: 'success', label: 'Active' };
    }
  };

  const formatCurrency = (value: any) => {
    if (!value || isNaN(Number(value))) return '-';
    return `$${Number(value).toLocaleString()}`;
  };

  return (
    <Card sx={{ mb: 3, border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <ShieldIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
          <Typography variant="h5" fontWeight="bold" color="primary">
            Insurance Coverage
          </Typography>
        </Box>

        {insurance ? (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight="medium">
                      {insurance.policyNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {insurance.insurerName}
                      {insurance.policyName && ` - ${insurance.policyName}`}
                    </Typography>
                  </Box>
                  <Chip
                    label={getExpiryStatus(insurance.expiryDate).label}
                    color={getExpiryStatus(insurance.expiryDate).color as any}
                    size="small"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="medium">
                    Effective Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {insurance.effectiveDate ? new Date(insurance.effectiveDate).toLocaleDateString() : '-'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="medium">
                    Expiry Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {insurance.expiryDate ? new Date(insurance.expiryDate).toLocaleDateString() : '-'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="medium">
                    Liability Limit
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {formatCurrency(insurance.liabilityLimit)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="medium">
                    Collision Deductible
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {formatCurrency(insurance.collisionDeductible)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="medium">
                    Comprehensive Deductible
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {formatCurrency(insurance.comprehensiveDeductible)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="medium">
                    All Perils Deductible
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {formatCurrency(insurance.allPerilsDeductible)}
                  </Typography>
                </Box>
              </Grid>

              {insurance.notes && (
                <Grid item xs={12}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Notes
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {insurance.notes}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>

            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <MuiButton
                size="small"
                onClick={() => setDialogOpen(true)}
              >
                View Policy Details
              </MuiButton>
            </Box>
          </Box>
        ) : (
          <Box sx={{
            textAlign: 'center',
            p: 3,
            backgroundColor: '#fafafa',
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <ShieldIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No insurance policy assigned
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Assign an insurance policy in the edit view
            </Typography>
          </Box>
        )}
      </CardContent>
      <InsuranceDetailsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        insurance={insurance}
      />
    </Card>
  );
};

// Document Upload Dialog Component (similar to PhotoUploadDialog)
const DocumentUploadDialog = ({ open, onClose, vehicleId, currentFile, onFileUpdated, documentType, label }: {
  open: boolean;
  onClose: () => void;
  vehicleId: number;
  currentFile?: { url: string; name: string };
  onFileUpdated: (fileUrl: string, fileName: string) => void;
  documentType: 'ownershipPermit' | 'weightRating';
  label: string;
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>(currentFile?.url || '');
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const validateFile = (file: File): boolean => {
    // Validate file size (max 10MB for documents)
    if (file.size > 10 * 1024 * 1024) {
      notify('File size must be less than 10MB', { type: 'error' });
      return false;
    }
    
    return true;
  };

  const handleFileSelect = (file: File) => {
    if (!validateFile(file)) return;

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const uploadToServer = async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', `equipment-${vehicleId}-${documentType}`);
    formData.append('description', `${label} for equipment ${vehicleId}`);
    formData.append('folder', 'equipment-documents');
    formData.append('isPublic', 'true');

    const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
    const accessToken = localStorage.getItem('accessToken');

    const response = await fetch(`${baseUrl}/files`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    return response.json();
  };

  const updateEquipmentDocument = async (fileId: number) => {
    const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
    const accessToken = localStorage.getItem('accessToken');

    const fieldName = documentType === 'ownershipPermit' ? 'ownershipPermitFileId' : 'weightRatingFileId';
    
    const response = await fetch(`${baseUrl}/equipment/${vehicleId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        [fieldName]: fileId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update equipment: ${response.statusText}`);
    }

    return response.json();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      // Upload file to server
      const fileData = await uploadToServer(selectedFile);
      
      // Update equipment with document file ID
      await updateEquipmentDocument(fileData.id);
      
      // Update UI
      onFileUpdated(fileData.url, fileData.name);
      notify(`${label} uploaded successfully`, { type: 'success' });
      refresh();
      onClose();
    } catch (error: any) {
      notify(`Failed to upload ${label}: ${error.message}`, { type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveDocument = async () => {
    setUploading(true);
    try {
      const fieldName = documentType === 'ownershipPermit' ? 'ownershipPermitFileId' : 'weightRatingFileId';
      
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      
      const response = await fetch(`${baseUrl}/equipment/${vehicleId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          [fieldName]: null,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update equipment: ${response.statusText}`);
      }
      
      setSelectedFile(null);
      setPreviewUrl('');
      onFileUpdated('', '');
      notify(`${label} removed`, { type: 'success' });
      refresh();
      onClose();
    } catch (error: any) {
      notify(`Failed to remove ${label}: ${error.message}`, { type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const isImage = (filename: string) => {
    return /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(filename);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {label}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          {previewUrl ? (
            isImage(selectedFile?.name || currentFile?.name || '') ? (
              <Box
                component="img"
                src={previewUrl}
                alt={label}
                sx={{
                  maxWidth: '100%',
                  maxHeight: 300,
                  borderRadius: 2,
                  mb: 2,
                }}
              />
            ) : (
              <Box sx={{ mb: 2, p: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <InsertDriveFileIcon sx={{ fontSize: 64, color: '#1976d2', mb: 1 }} />
                <Typography variant="body1">{selectedFile?.name || currentFile?.name}</Typography>
              </Box>
            )
          ) : (
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById(`${documentType}-file-input`)?.click()}
              sx={{
                width: '100%',
                height: 200,
                border: isDragOver ? '2px dashed #1976d2' : '2px dashed #ccc',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                backgroundColor: isDragOver ? '#f3f7ff' : '#f9f9f9',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: '#f3f7ff',
                },
              }}
            >
              <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                <UploadIcon sx={{ fontSize: 48, mb: 1, color: isDragOver ? '#1976d2' : 'inherit' }} />
                <Typography variant="body2" sx={{ color: isDragOver ? '#1976d2' : 'inherit' }}>
                  {isDragOver ? 'Drop file here' : 'Drag and drop file here or click to select'}
                </Typography>
              </Box>
            </Box>
          )}
          
          <input
            accept="image/*,.pdf,.doc,.docx"
            style={{ display: 'none' }}
            id={`${documentType}-file-input`}
            type="file"
            onChange={handleInputChange}
          />
          <label htmlFor={`${documentType}-file-input`}>
            <MuiButton
              variant="outlined"
              component="span"
              startIcon={<InsertDriveFileIcon />}
              sx={{ mr: 1 }}
            >
              Select File
            </MuiButton>
          </label>
          
          {currentFile && (
            <MuiButton
              variant="outlined"
              color="error"
              onClick={handleRemoveDocument}
              sx={{ ml: 1 }}
            >
              Remove Current {label}
            </MuiButton>
          )}
          
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
            Maximum file size: 10MB • Supported formats: Images, PDF, DOC, DOCX
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>Cancel</MuiButton>
        <MuiButton
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

// Document Display Component (similar to vehicle photo display)
const DocumentDisplay = ({ file, fileId, label, documentType, vehicleId }: {
  file?: { url: string; name: string };
  fileId?: string | number;
  label: string;
  documentType: 'ownershipPermit' | 'weightRating';
  vehicleId: number;
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentFile, setCurrentFile] = useState(file);
  
  React.useEffect(() => {
    setCurrentFile(file);
  }, [file]);

  const handleFileUpdated = (fileUrl: string, fileName: string) => {
    if (fileUrl && fileName) {
      setCurrentFile({ url: fileUrl, name: fileName });
    } else {
      setCurrentFile(undefined);
    }
  };

  const isImage = (filename?: string) => {
    if (!filename) return false;
    return /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(filename);
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: 250,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          border: '2px dashed #ddd',
          '&:hover': {
            backgroundColor: '#f0f0f0',
            borderColor: '#1976d2',
          },
        }}
        onClick={() => setDialogOpen(true)}
      >
        {currentFile?.url ? (
          isImage(currentFile.name) ? (
            <Box
              component="img"
              src={currentFile.url}
              alt={label}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
              <InsertDriveFileIcon sx={{ fontSize: 64, mb: 1, color: '#1976d2' }} />
              <Typography variant="body2" sx={{ px: 2 }} noWrap>
                {currentFile.name}
              </Typography>
              <Typography variant="caption">
                Click to view or replace
              </Typography>
            </Box>
          )
        ) : (
          <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
            <InsertDriveFileIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body2">
              Click to upload {label.toLowerCase()}
            </Typography>
          </Box>
        )}
      </Box>
      
      <DocumentUploadDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        vehicleId={vehicleId}
        currentFile={currentFile}
        onFileUpdated={handleFileUpdated}
        documentType={documentType}
        label={label}
      />
    </>
  );
};

// Documents Section Component (View Only)
const DocumentsSectionViewOnly = () => {
  const record = useRecordContext();

  if (!record) return null;

  // Extract equipment files from the record
  const equipmentFiles = record.allFiles?.filter(f => f.entityType === 'equipment') || [];

  return (
    <Card sx={{ mb: 3, border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <InsertDriveFileIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
          <Typography variant="h5" fontWeight="bold" color="primary">
            Documents & Files
          </Typography>
        </Box>
        <EquipmentPrimaryFiles
          equipmentId={record.id}
          files={equipmentFiles}
        />
      </CardContent>
    </Card>
  );
};

// Custom field components
const StatusField = () => {
  const record = useRecordContext();
  if (!record?.status) return <span></span>;
  
  const getColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'MAINTENANCE': return 'warning';
      case 'INACTIVE': return 'default';
      case 'RETIRED': return 'info';
      case 'DELETED': return 'error';
      default: return 'default';
    }
  };
  
  return (
    <Chip 
      label={String(record.status)} 
      color={getColor(record.status)}
      size="small"
      variant="outlined"
    />
  );
};

const TypeField = () => {
  const record = useRecordContext();
  if (!record?.type) return <span></span>;
  
  return (
    <Chip 
      label={String(record.type).replace(/_/g, ' ')} 
      color="primary"
      size="small"
    />
  );
};

const OwnershipField = () => {
  const record = useRecordContext();
  if (!record?.ownershipType) return <span></span>;
  
  return (
    <Chip 
      label={String(record.ownershipType)} 
      color="info"
      size="small"
      variant="outlined"
    />
  );
};

const CurrencyField = ({ source }: { source: string }) => {
  const record = useRecordContext();
  const value = record?.[source];
  
  if (!value || isNaN(Number(value))) return <span></span>;
  
  return (
    <span>${Number(value).toLocaleString()}</span>
  );
};

// Custom field for fuel type display
const FuelTypeField = () => {
  const record = useRecordContext();
  const value = record?.fuelType;
  
  const fuelTypeMap: Record<string, string> = {
    'DIESEL': 'Diesel',
    'GAS': 'Gasoline',
    'DEF': 'Diesel Exhaust Fluid',
    'HYDRAULIC': 'Hydraulic Fluid',
    'ENGINE_OIL': 'Engine Oil',
    'OTHER': 'Other'
  };
  
  return <span>{fuelTypeMap[value] || value || ''}</span>;
};

// Custom field for service type display
const ServiceTypeField = () => {
  const record = useRecordContext();
  const value = record?.serviceType;
  
  const serviceTypeMap: Record<string, string> = {
    'OIL_CHANGE': 'Oil Change',
    'INSPECTION': 'Inspection',
    'REPAIR': 'Repair',
    'TIRE_SERVICE': 'Tire Service',
    'BRAKE_SERVICE': 'Brake Service',
    'ENGINE_SERVICE': 'Engine Service',
    'TRANSMISSION_SERVICE': 'Transmission Service',
    'ELECTRICAL': 'Electrical',
    'BODYWORK': 'Bodywork',
    'PREVENTIVE': 'Preventive Maintenance',
    'EMERGENCY': 'Emergency Repair',
    'OTHER': 'Other'
  };
  
  return <span>{serviceTypeMap[value] || value || ''}</span>;
};

// Custom field for inspection type display
const InspectionTypeField = () => {
  const record = useRecordContext();
  const value = record?.inspectionType;
  
  const inspectionTypeMap: Record<string, string> = {
    'ANNUAL': 'Annual Inspection',
    'DAILY': 'Daily Inspection',
    'WEEKLY': 'Weekly Inspection',
    'MONTHLY': 'Monthly Inspection',
    'PRE_TRIP': 'Pre-Trip Inspection',
    'POST_TRIP': 'Post-Trip Inspection',
    'DOT': 'DOT Inspection',
    'CVOR': 'CVOR Inspection',
    'SAFETY': 'Safety Inspection',
    'EMISSIONS': 'Emissions Inspection',
    'BRAKE': 'Brake Inspection',
    'TIRE': 'Tire Inspection',
    'ENGINE': 'Engine Inspection',
    'ELECTRICAL': 'Electrical Inspection',
    'OTHER': 'Other'
  };
  
  return <span>{inspectionTypeMap[value] || value || ''}</span>;
};

// Custom field for condition display in assignments
const ConditionField = () => {
  const record = useRecordContext();
  const value = record?.condition;
  
  const conditionMap: Record<string, string> = {
    'EXCELLENT': 'Excellent',
    'GOOD': 'Good',
    'FAIR': 'Fair',
    'POOR': 'Poor',
    'DAMAGED': 'Damaged'
  };
  
  return <span>{conditionMap[value] || value || ''}</span>;
};

// Custom field for incident type display
const IncidentTypeField = () => {
  const record = useRecordContext();
  const value = record?.incidentType;
  
  const incidentTypeMap: Record<string, string> = {
    'ACCIDENT': 'Accident',
    'MINOR_ACCIDENT': 'Minor Accident',
    'MAJOR_ACCIDENT': 'Major Accident',
    'BREAKDOWN': 'Breakdown',
    'MECHANICAL_FAILURE': 'Mechanical Failure',
    'DAMAGE': 'Damage',
    'THEFT': 'Theft',
    'VANDALISM': 'Vandalism',
    'FIRE': 'Fire',
    'FLOOD': 'Flood',
    'COLLISION': 'Collision',
    'TRAFFIC_VIOLATION': 'Traffic Violation',
    'OTHER': 'Other'
  };
  
  return <span>{incidentTypeMap[value] || value || ''}</span>;
};

// Custom field for driver name display with error handling
const DriverNameField = () => {
  const record = useRecordContext();
  
  if (!record?.driverId) {
    return <span></span>;
  }
  
  // If driver data is populated
  if (record.driver?.user?.name) {
    return <span>{record.driver.user.name}</span>;
  }
  
  // Fallback display
  return <span>Driver {record.driverId}</span>;
};

// Custom Edit Button Component
const CustomEditButton = ({ onClick }: { onClick: (id: string | number) => void }) => {
  const record = useRecordContext();
  
  return (
    <IconButton
      onClick={() => onClick(record?.id)}
      size="small"
      color="primary"
      title="Edit"
    >
      <EditIcon />
    </IconButton>
  );
};

// Dialog Components - now imported from separate files

// Tab Components
const MaintenanceTab = () => {
  const record = useRecordContext();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [showDialogOpen, setShowDialogOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  
  const handleRowClick = (id: string | number, resource?: any) => {
    console.log('MaintenanceTab handleRowClick fired with id:', id);
    setSelectedRecordId(Number(id));
    setShowDialogOpen(true);
    return false; // Prevent default navigation
  };
  
  const handleEdit = () => {
    setShowDialogOpen(false);
    setEditDialogOpen(true);
  };
  
  const handleCloseDialogs = () => {
    setEditDialogOpen(false);
    setShowDialogOpen(false);
    setSelectedRecordId(null);
  };
  
  return (
    <ReferenceManyField
      reference="equipment-maintenance"
      target="equipmentId"
      label={false}
    >
      <Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary">
            Maintenance Records
          </Typography>
          <MuiButton
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<CreateIcon />}
            variant="contained"
            size="small"
          >
            Add Maintenance Record
          </MuiButton>
        </Box>
        <MaintenanceDialog 
          open={createDialogOpen} 
          onClose={() => setCreateDialogOpen(false)} 
          equipmentId={record?.id} 
        />
        <MaintenanceDialog 
          open={editDialogOpen} 
          onClose={handleCloseDialogs} 
          equipmentId={record?.id}
          recordId={selectedRecordId}
          isEdit={true}
        />
        {selectedRecordId && (
          <MaintenanceShowDialog
            open={showDialogOpen}
            onClose={handleCloseDialogs}
            recordId={selectedRecordId}
            onEdit={handleEdit}
          />
        )}
        <Datagrid
          bulkActionButtons={false}
          rowClick={handleRowClick}
          sx={{
            '& .RaDatagrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
          empty={
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <MaintenanceIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No maintenance records yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Start tracking vehicle maintenance by adding your first service record
              </Typography>
            </Box>
          }
        >
          <DateField source="serviceDate" label="Service Date" />
          <ServiceTypeField label="Service Type" />
          <ReferenceField 
            source="serviceProviderId" 
            reference="service-providers" 
            label="Service Provider"
            emptyText="No Provider"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
          <SafeNumberField source="serviceMileage" label="Odometer (KM)" />
          <SafeNumberField source="totalCost" label="Total Cost ($)" />
          <BooleanField source="scheduledService" label="Scheduled" />
        </Datagrid>
      </Box>
    </ReferenceManyField>
  );
};


const FuelTab = () => {
  const record = useRecordContext();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [showDialogOpen, setShowDialogOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  
  const handleRowClick = (id: string | number, resource?: any) => {
    console.log('FuelTab handleRowClick fired with id:', id);
    setSelectedRecordId(Number(id));
    setShowDialogOpen(true);
    return false; // Prevent default navigation
  };
  
  return (
    <ReferenceManyField
      reference="equipment-fuel"
      target="equipmentId"
      label={false}
    >
      <Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary">
            Fuel Records
          </Typography>
          <MuiButton
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<CreateIcon />}
            variant="contained"
            size="small"
          >
            Add Fuel Record
          </MuiButton>
        </Box>
        <FuelDialog 
          open={createDialogOpen} 
          onClose={() => setCreateDialogOpen(false)} 
          equipmentId={record?.id} 
        />
        {selectedRecordId && (
          <FuelShowDialog
            open={showDialogOpen}
            onClose={() => {
              setShowDialogOpen(false);
              setSelectedRecordId(null);
            }}
            recordId={selectedRecordId}
            equipmentId={record?.id}
          />
        )}
        <Datagrid
          bulkActionButtons={false}
          rowClick={handleRowClick}
          sx={{
            '& .RaDatagrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
          empty={
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <FuelIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No fuel records yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track vehicle fuel purchases and consumption to monitor efficiency
              </Typography>
            </Box>
          }
        >
          <DateField source="fuelDate" label="Fuel Date" />
          <FuelTypeField label="Fuel Type" />
          <SafeNumberField source="quantity" label="Quantity" />
          <SafeNumberField source="unitCost" label="Unit Cost ($)" />
          <SafeNumberField source="totalCost" label="Total Cost ($)" />
          <SafeNumberField source="mileage" label="Odometer (KM)" />
          <SafeTextField source="location" label="Location" />
        </Datagrid>
      </Box>
    </ReferenceManyField>
  );
};

const InspectionsTab = () => {
  const record = useRecordContext();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [showDialogOpen, setShowDialogOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  
  const handleRowClick = (id: string | number, resource?: any) => {
    console.log('InspectionsTab handleRowClick fired with id:', id);
    setSelectedRecordId(Number(id));
    setShowDialogOpen(true);
    return false; // Prevent default navigation
  };
  
  return (
    <ReferenceManyField
      reference="equipment-inspections"
      target="equipmentId"
      label={false}
    >
      <Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary">
            Inspections
          </Typography>
          <MuiButton
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<CreateIcon />}
            variant="contained"
            size="small"
          >
            Add Inspection
          </MuiButton>
        </Box>
        <InspectionDialog 
          open={createDialogOpen} 
          onClose={() => setCreateDialogOpen(false)} 
          equipmentId={record?.id} 
        />
        {selectedRecordId && (
          <InspectionShowDialog
            open={showDialogOpen}
            onClose={() => {
              setShowDialogOpen(false);
              setSelectedRecordId(null);
            }}
            recordId={selectedRecordId}
            equipmentId={record?.id}
          />
        )}
        <Datagrid
          bulkActionButtons={false}
          rowClick={handleRowClick}
          sx={{
            '& .RaDatagrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
          empty={
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <InspectionIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No inspections yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Stay compliant by recording vehicle safety inspections and checks
              </Typography>
            </Box>
          }
        >
          <DateField source="inspectionDate" label="Inspection Date" />
          <InspectionTypeField label="Inspection Type" />
          <SafeTextField source="inspector" label="Inspector" />
          <BooleanField source="passed" label="Passed" />
          <DateField source="nextDueDate" label="Next Due Date" />
        </Datagrid>
      </Box>
    </ReferenceManyField>
  );
};

const AssignmentsTab = () => {
  const record = useRecordContext();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRecordId, setEditRecordId] = useState(null);
  
  const handleEdit = (id: string | number) => {
    setEditRecordId(Number(id));
    setEditDialogOpen(true);
  };
  
  return (
    <ReferenceManyField
      reference="equipment-assignments"
      target="equipmentId"
      label={false}
    >
      <Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary">
            Assignment History
          </Typography>
          <MuiButton
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<CreateIcon />}
            variant="contained"
            size="small"
          >
            Add Assignment
          </MuiButton>
        </Box>
        <AssignmentDialog 
          open={createDialogOpen} 
          onClose={() => setCreateDialogOpen(false)} 
          equipmentId={record?.id} 
        />
        <AssignmentDialog 
          open={editDialogOpen} 
          onClose={() => setEditDialogOpen(false)} 
          equipmentId={record?.id}
          recordId={editRecordId}
          isEdit={true}
        />
        <Datagrid
          bulkActionButtons={false}
          rowClick={(id) => handleEdit(id)}
          sx={{
            '& .RaDatagrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
          empty={
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No assignments yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track who uses this vehicle and when
              </Typography>
            </Box>
          }
        >
          <DriverNameField label="Driver" />
          <ConditionField label="Condition" />
          <DateField source="assignedDate" label="Assigned Date" />
          <NumberField source="mileageOut" label="Odometer Out (KM)" />
          <DateField source="returnedDate" label="Returned Date" />
          <NumberField source="mileageIn" label="Odometer In (KM)" />
          <SafeTextField source="notes" label="Notes" />
          <CustomEditButton onClick={handleEdit} />
        </Datagrid>
      </Box>
    </ReferenceManyField>
  );
};

const DocumentsTab = () => {
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Box sx={{ p: 3 }}>
      <EquipmentDocuments equipmentId={record.id} />
    </Box>
  );
};


const CompletedFormsTab = () => {
  const record = useRecordContext();
  const [showDialog, setShowDialog] = React.useState(false);
  const [selectedFormId, setSelectedFormId] = React.useState<number | null>(null);
  const [formTypeFilter, setFormTypeFilter] = React.useState<number | null>(null);

  // Fetch forms for the dropdown
  const { data: forms } = useGetList('forms', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });

  const handleRowClick = (id: string | number) => {
    setSelectedFormId(Number(id));
    setShowDialog(true);
    return false; // Prevent default navigation
  };

  if (!record) return null;

  // Build filter object - always include equipmentId, optionally include formId
  const filter = formTypeFilter
    ? { equipmentId: record.id, formId: formTypeFilter }
    : { equipmentId: record.id };

  return (
    <ReferenceManyField
      reference="completed-forms/reports"
      target="equipmentId"
      label={false}
      perPage={10}
      pagination={<></>}
      filter={filter}
    >
      <Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary">
            Completed Forms for this Vehicle
          </Typography>
        </Box>

        {/* Form Type Filter */}
        <Box sx={{ mb: 2 }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Form Type</InputLabel>
            <Select
              value={formTypeFilter || ''}
              onChange={(event) => setFormTypeFilter(event.target.value || null)}
              label="Form Type"
              sx={{
                minWidth: 250,
                height: '56px',
              }}
            >
              <MenuItem value="">All Form Types</MenuItem>
              {forms?.map(form => (
                <MenuItem key={form.id} value={form.id}>
                  {form.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Datagrid
          bulkActionButtons={false}
          rowClick={handleRowClick}
          sx={{
            '& .RaDatagrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
          empty={
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CompletedFormsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No completed forms yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Forms completed for this vehicle will appear here
              </Typography>
            </Box>
          }
        >
          <FunctionField
            label="Started At"
            render={(record: any) => new Date(record.startedAt).toLocaleString()}
            source="startedAt"
          />
          <FunctionField
            label="Completed At"
            render={(record: any) => new Date(record.completedAt).toLocaleString()}
            source="completedAt"
          />
          <FunctionField
            label="Completed By"
            render={(record: any) => record.user?.name || record.user?.email}
          />
          <FunctionField label="Form Type" render={(record: any) => record.form?.name} />
        </Datagrid>
        <CompletedFormDialog
          open={showDialog}
          onClose={() => {
            setShowDialog(false);
            setSelectedFormId(null);
          }}
          formId={selectedFormId}
        />
      </Box>
    </ReferenceManyField>
  );
};

const IncidentsTab = () => {
  const record = useRecordContext();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [showDialogOpen, setShowDialogOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  
  const handleRowClick = (id: string | number, resource?: any) => {
    console.log('IncidentsTab handleRowClick fired with id:', id);
    setSelectedRecordId(Number(id));
    setShowDialogOpen(true);
    return false; // Prevent default navigation
  };
  
  return (
    <ReferenceManyField
      reference="equipment-incidents"
      target="equipmentId"
      label={false}
    >
      <Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary">
            Incident Reports
          </Typography>
          <MuiButton
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<CreateIcon />}
            variant="contained"
            size="small"
          >
            Add Incident
          </MuiButton>
        </Box>
        <IncidentDialog 
          open={createDialogOpen} 
          onClose={() => setCreateDialogOpen(false)} 
          equipmentId={record?.id} 
        />
        {selectedRecordId && (
          <IncidentShowDialog
            open={showDialogOpen}
            onClose={() => {
              setShowDialogOpen(false);
              setSelectedRecordId(null);
            }}
            recordId={selectedRecordId}
            equipmentId={record?.id}
          />
        )}
        <Datagrid
          bulkActionButtons={false}
          rowClick={handleRowClick}
          sx={{
            '& .RaDatagrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
          empty={
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <IncidentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No incidents reported
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Document any accidents, damages, or issues with this vehicle
              </Typography>
            </Box>
          }
        >
          <DateField source="incidentDate" label="Incident Date" />
          <IncidentTypeField label="Incident Type" />
          <SafeTextField source="description" label="Description" />
          <DriverNameField label="Driver" />
          <NumberField source="repairCost" label="Repair Cost ($)" />
          <BooleanField source="resolved" label="Resolved" />
        </Datagrid>
      </Box>
    </ReferenceManyField>
  );
};


// Tab Label with Count Component
const TabLabelWithCount = ({ title, color }: { title: string; color: string }) => {
  const record = useRecordContext();
  
  // You would typically get these counts from the data
  // For now, we'll use placeholder logic
  const getCount = (tabType: string) => {
    // This would be replaced with actual count logic
    // based on the related data for each tab
    switch (tabType.toLowerCase()) {
      case 'maintenance':
        return record?.maintenanceCount || 0;
      case 'fuel records':
        return record?.fuelCount || 0;
      case 'inspections':
        return record?.inspectionCount || 0;
      case 'assignments':
        return record?.assignmentCount || 0;
      case 'incidents':
        return record?.incidentCount || 0;
      default:
        return 0;
    }
  };
  
  const count = getCount(title);
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <span>{title}</span>
      {count > 0 && (
        <Chip
          label={count}
          size="small"
          sx={{
            backgroundColor: color,
            color: 'white',
            fontWeight: 'bold',
            minWidth: '20px',
            height: '20px',
            '& .MuiChip-label': {
              padding: '0 6px',
              fontSize: '0.75rem',
            },
          }}
        />
      )}
    </Box>
  );
};

// Photo Upload Dialog Component
const PhotoUploadDialog = ({ open, onClose, vehicleId, currentPhoto, onPhotoUpdated }: {
  open: boolean;
  onClose: () => void;
  vehicleId: number;
  currentPhoto?: string;
  onPhotoUpdated: (photoUrl: string) => void;
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>(currentPhoto || '');
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const validateFile = (file: File): boolean => {
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      notify('File size must be less than 5MB', { type: 'error' });
      return false;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      notify('Please select an image file', { type: 'error' });
      return false;
    }

    return true;
  };

  const handleFileSelect = (file: File) => {
    if (!validateFile(file)) return;

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const uploadAndSetPrimaryPhoto = async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);

    const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
    const accessToken = localStorage.getItem('accessToken');

    const response = await fetch(`${baseUrl}/equipment/${vehicleId}/upload-primary-photo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    return response.json();
  };

  const createEntityImage = async (fileId: number) => {
    const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
    const accessToken = localStorage.getItem('accessToken');

    const response = await fetch(`${baseUrl}/equipment/images`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        entityType: 'equipment',
        entityId: vehicleId,
        fileId: fileId,
        imageType: 'VEHICLE',
        caption: 'Primary vehicle photo',
        displayOrder: 0,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create entity image: ${response.statusText}`);
    }

    return response.json();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      // Use the new combined endpoint
      const result = await uploadAndSetPrimaryPhoto(selectedFile);
      
      // Update the UI with the new photo
      if (result.primaryPhoto && result.primaryPhoto.url) {
        onPhotoUpdated(result.primaryPhoto.url);
      }
      
      notify('Photo uploaded successfully', { type: 'success' });
      refresh();
      onClose();
    } catch (error: any) {
      notify(`Failed to upload photo: ${error.message}`, { type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    setUploading(true);
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');

      const response = await fetch(`${baseUrl}/equipment/${vehicleId}/primary-photo`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to remove photo: ${response.statusText}`);
      }
      
      setSelectedFile(null);
      setPreviewUrl('');
      onPhotoUpdated('');
      notify('Photo removed', { type: 'success' });
      refresh();
      onClose();
    } catch (error: any) {
      notify(`Failed to remove photo: ${error.message}`, { type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Vehicle Photo
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt="Vehicle preview"
              sx={{
                maxWidth: '100%',
                maxHeight: 300,
                borderRadius: 2,
                mb: 2,
              }}
            />
          ) : (
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('photo-file-input')?.click()}
              sx={{
                width: '100%',
                height: 200,
                border: isDragOver ? '2px dashed #1976d2' : '2px dashed #ccc',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                backgroundColor: isDragOver ? '#f3f7ff' : '#f9f9f9',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: '#f3f7ff',
                },
              }}
            >
              <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                <UploadIcon sx={{ fontSize: 48, mb: 1, color: isDragOver ? '#1976d2' : 'inherit' }} />
                <Typography variant="body2" sx={{ color: isDragOver ? '#1976d2' : 'inherit' }}>
                  {isDragOver ? 'Drop photo here' : 'Drag and drop photo here or click to select'}
                </Typography>
              </Box>
            </Box>
          )}
          
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="photo-file-input"
            type="file"
            onChange={handleInputChange}
          />
          <label htmlFor="photo-file-input">
            <MuiButton
              variant="outlined"
              component="span"
              startIcon={<PhotoIcon />}
              sx={{ mr: 1 }}
            >
              Select Photo
            </MuiButton>
          </label>
          
          {currentPhoto && (
            <MuiButton
              variant="outlined"
              color="error"
              onClick={handleRemovePhoto}
              sx={{ ml: 1 }}
            >
              Remove Current Photo
            </MuiButton>
          )}
          
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
            Maximum file size: 5MB • Supported formats: JPG, PNG, WebP
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>Cancel</MuiButton>
        <MuiButton
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
        >
          {uploading ? 'Uploading...' : 'Upload Photo'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

// Vehicle Summary Card Component
const VehicleSummaryCard = () => {
  const record = useRecordContext();
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [currentPhoto, setCurrentPhoto] = useState(record?.primaryPhoto?.url || '');
  
  // Update photo when record changes
  React.useEffect(() => {
    setCurrentPhoto(record?.primaryPhoto?.url || '');
  }, [record?.primaryPhoto?.url, record?.primaryPhotoId]);
  
  if (!record) return null;
  
  const handlePhotoUpdated = (photoUrl: string) => {
    setCurrentPhoto(photoUrl);
  };
  
  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            {/* Vehicle Photo */}
            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  width: '100%',
                  height: 180,
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: '2px dashed #ddd',
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                    borderColor: '#1976d2',
                  },
                }}
                onClick={() => setPhotoDialogOpen(true)}
              >
                {currentPhoto ? (
                  <Box
                    component="img"
                    src={currentPhoto}
                    alt={`${record.name} photo`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    <PhotoIcon sx={{ fontSize: 48, mb: 1 }} />
                    <Typography variant="body2">
                      Click to add photo
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          
          {/* Vehicle Info */}
          <Grid item xs={12} md={9}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {record.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TypeField />
              <StatusField />
              <OwnershipField />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Year • Make • Model
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {record.manufacturedYear || '-'} • {record.make || '-'} • {record.model || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  License Plate
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {record.licensePlate || '-'}
                  {record.jurisdiction && ` (${record.jurisdiction})`}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  VIN
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {record.vin || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Transmission
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {record.transmission || '-'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          
        </Grid>
      </CardContent>
      
      <PhotoUploadDialog
        open={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        vehicleId={record.id}
        currentPhoto={currentPhoto}
        onPhotoUpdated={handlePhotoUpdated}
      />
    </Card>
    </>
  );
};

export const EquipmentShow = () => (
  <Show actions={false}>
    <ResourceBreadcrumbs />
    <VehicleSummaryCard />
    <TabbedShowLayout>
      <Tab label="Overview" icon={<InfoIcon />}>
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
          {/* Actions Section */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <EditButton />
          </Box>

          {/* Identification Section */}
          <Card sx={{ mb: 3, overflow: 'visible', border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <VehicleIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
                <Typography variant="h5" fontWeight="bold" color="primary">
                  Vehicle Identification
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">VIN</Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, wordBreak: 'break-all' }}>
                      <SafeTextField source="vin" />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">License Plate</Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      <SafeTextField source="licensePlate" />
                      <FunctionField 
                        source="jurisdiction" 
                        render={(record: any) => {
                          if (!record.jurisdiction) return '';
                          return ` (${record.jurisdiction})`;
                        }}
                      />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">Max Weight (kg)</Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      <SafeNumberField source="maxHauledKg" />
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Specifications Section */}
          <Card sx={{ mb: 3, border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <InfoIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
                <Typography variant="h5" fontWeight="bold" color="primary">
                  Specifications
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">Year</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <SafeTextField source="manufacturedYear" />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">Make</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <SafeTextField source="make" />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">Model</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <SafeTextField source="model" />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">Transmission</Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      <SafeTextField source="transmission" />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">Odometer (KM)</Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      <SafeNumberField source="odometer" />
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Financial Information */}
          <Card sx={{ mb: 3, border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <InfoIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
                <Typography variant="h5" fontWeight="bold" color="primary">
                  Financial Information
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">Acquisition Date</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <FunctionField 
                        source="acquireDate" 
                        render={(record: any) => {
                          if (!record.acquireDate) return '-';
                          return new Date(record.acquireDate).toLocaleDateString();
                        }}
                      />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">List Price New</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <CurrencyField source="listPriceNew" />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">Actual Cash Value</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <CurrencyField source="actualCashValue" />
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Job Types Section */}
          <JobTypesSection />

          {/* Commodities Section */}
          <CommoditiesSection />

          {/* Insurance Section */}
          <InsuranceSection />

          {/* CVOR Section */}
          <CvorSection />

          {/* Notes Section */}
          <Card sx={{ mb: 3, border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
                Additional Notes
              </Typography>
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#f5f5f5', 
                borderRadius: 2,
                minHeight: 60,
                border: '1px solid #e0e0e0'
              }}>
                <SafeTextField source="notes" />
              </Box>
            </CardContent>
          </Card>

          {/* Documents Section */}
          <DocumentsSectionViewOnly />
        </Box>
      </Tab>

      <Tab 
        label={<TabLabelWithCount title="Maintenance" color="#ed6c02" />} 
        icon={<MaintenanceIcon sx={{ color: '#ed6c02' }} />}
      >
        <MaintenanceTab />
      </Tab>

      <Tab 
        label={<TabLabelWithCount title="Fuel Records" color="#0288d1" />} 
        icon={<FuelIcon sx={{ color: '#0288d1' }} />}
      >
        <FuelTab />
      </Tab>

      {/* Inspections tab - hidden for now, will be re-added later
      <Tab 
        label={<TabLabelWithCount title="Inspections" color="#2e7d32" />} 
        icon={<InspectionIcon sx={{ color: '#2e7d32' }} />}
      >
        <InspectionsTab />
      </Tab>
      */}

      <Tab 
        label={<TabLabelWithCount title="Assignments" color="#1976d2" />} 
        icon={<AssignmentIcon sx={{ color: '#1976d2' }} />}
      >
        <AssignmentsTab />
      </Tab>

      <Tab 
        label={<TabLabelWithCount title="Incidents" color="#d32f2f" />} 
        icon={<IncidentIcon sx={{ color: '#d32f2f' }} />}
      >
        <IncidentsTab />
      </Tab>

      <Tab
        label={<TabLabelWithCount title="Reminders" color="#9c27b0" />}
        icon={<RemindersIcon sx={{ color: '#9c27b0' }} />}
      >
        <RemindersTab />
      </Tab>

      <Tab
        label={<TabLabelWithCount title="Completed Forms" color="#4caf50" />}
        icon={<CompletedFormsIcon sx={{ color: '#4caf50' }} />}
      >
        <CompletedFormsTab />
      </Tab>

      <Tab
        label={<TabLabelWithCount title="Documents" color="#0288d1" />}
        icon={<FilesIcon sx={{ color: '#0288d1' }} />}
      >
        <DocumentsTab />
      </Tab>
    </TabbedShowLayout>
  </Show>
);

export default EquipmentShow;